import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Phone, Mail, Briefcase, Calendar, Heart, ArrowLeft, Users as UsersIcon, FileText, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';

const API = `${API_URL}/api`;

const MemberDetailPage = () => {
  const { userId } = useParams();
  const [member, setMember] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMemberData();
  }, [userId]);

  const loadMemberData = async () => {
    try {
      const [memberRes, childrenRes] = await Promise.all([
        axios.get(`${API}/members/${userId}`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } }),
        axios.get(`${API}/members/${userId}/children`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } })
      ]);
      setMember(memberRes.data);
      setChildren(childrenRes.data);
    } catch (error) {
      console.error('Error loading member:', error);
      toast.error('Failed to load member details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="text-center py-12">
        <p>Member not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="member-detail-page">
      <Link to="/app/members">
        <Button variant="ghost" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Members
        </Button>
      </Link>
      
      <Card>
        <CardContent className="p-8">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex flex-col items-center">
              <img
                src={getImageUrl(member.photo_url) || 'https://api.dicebear.com/7.x/avataaars/svg?seed=Lion'}
                alt={member.name}
                className="w-32 h-32 rounded-full object-cover mb-4"
              />
              <h2 className="text-2xl font-bold text-center mb-2" style={{ color: '#0F172A' }}>
                {member.name}
              </h2>
              <p className="text-sm px-3 py-1 rounded-full" style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}>
                {member.role}
              </p>
            </div>
            
            <div className="flex-1 space-y-6">
              <div>
                <h3 className="text-lg font-semibold mb-3">Contact</h3>
                <div className="space-y-3">
                  {member.mobile && (
                    <div className="flex items-center gap-3">
                      <Phone className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-sm text-muted-foreground">Mobile</p>
                        <p className="font-medium">{member.mobile}</p>
                        <div className="flex gap-2 mt-1">
                          <a
                            href={`tel:${member.mobile}`}
                            className="text-sm text-primary hover:underline"
                          >
                            Call
                          </a>
                          <span className="text-muted-foreground">•</span>
                          <a
                            href={`https://wa.me/${member.mobile.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline"
                          >
                            WhatsApp
                          </a>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <a href={`mailto:${member.email}`} className="font-medium hover:underline">
                        {member.email}
                      </a>
                    </div>
                  </div>
                  {member.address && (
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-muted-foreground mt-1" />
                      <div>
                        <p className="text-sm text-muted-foreground">Address</p>
                        <p className="font-medium">{member.address}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {member.professional_category && (
                <div>
                  <h3 className="text-lg font-semibold mb-3">Professional</h3>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-5 h-5 text-muted-foreground" />
                      <p className="font-medium">{member.professional_category}</p>
                    </div>
                    {member.professional_title && (
                      <p className="text-sm text-muted-foreground ml-7">{member.professional_title}</p>
                    )}
                    {member.company_name && (
                      <p className="text-sm text-muted-foreground ml-7">{member.company_name}</p>
                    )}
                    {member.office_address && (
                      <div className="flex items-start gap-2 mt-2">
                        <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                        <p className="text-sm text-muted-foreground">{member.office_address}</p>
                      </div>
                    )}
                    {member.business_card_url && (
                      <a
                        href={getImageUrl(member.business_card_url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-primary hover:underline ml-7"
                      >
                        <FileText className="w-4 h-4" />
                        View Business Card
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {(member.spouse_name || member.date_of_birth || member.wedding_anniversary_date) && (
        <Card>
          <CardHeader>
            <CardTitle>Personal & Family</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {member.date_of_birth && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-muted-foreground mt-1" />
                <div>
                  <p className="text-sm text-muted-foreground">Date of Birth</p>
                  <p className="font-medium">{formatDate(member.date_of_birth)}</p>
                </div>
              </div>
            )}
            
            {member.spouse_name && (
              <>
                <Separator />
                <div>
                  <h4 className="font-semibold mb-3">Spouse</h4>
                  <div className="flex items-start gap-4">
                    {member.spouse_photo_url && (
                      <img
                        src={getImageUrl(member.spouse_photo_url)}
                        alt={member.spouse_name}
                        className="w-16 h-16 rounded-full object-cover"
                      />
                    )}
                    <div className="space-y-2">
                      <p className="font-medium">{member.spouse_name}</p>
                      {member.spouse_date_of_birth && (
                        <p className="text-sm text-muted-foreground">
                          Born: {formatDate(member.spouse_date_of_birth)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
            
            {member.wedding_anniversary_date && (
              <>
                <Separator />
                <div className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-muted-foreground mt-1" />
                  <div>
                    <p className="text-sm text-muted-foreground">Wedding Anniversary</p>
                    <p className="font-medium">{formatDate(member.wedding_anniversary_date)}</p>
                  </div>
                </div>
              </>
            )}
            
            {member.joint_photo_url && (
              <>
                <Separator />
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Joint Photo</p>
                  <img
                    src={getImageUrl(member.joint_photo_url)}
                    alt="Joint photo"
                    className="w-full max-w-md rounded-lg"
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
      
      {children.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UsersIcon className="w-5 h-5" />
              Children
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {children.map((child) => (
                <div key={child.child_id} className="p-4 border rounded-lg">
                  <p className="font-medium">{child.name}</p>
                  <p className="text-sm text-muted-foreground capitalize">{child.gender}</p>
                  <p className="text-sm text-muted-foreground">
                    Born: {formatDate(child.date_of_birth)}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MemberDetailPage;
